import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Check,
  ChevronRight,
  Crosshair,
  Database,
  FileCode,
  Globe,
  History,
  Lock,
  Menu,
  Play,
  RefreshCw,
  Settings,
  X,
  Zap,
} from 'lucide-react';
import type { PublicHelpTopicPath, PublicRoutePath } from './publicRoutes';
import PublicContactForm from './PublicContactForm';
import FeatureRequestPage from './FeatureRequestPage';
import LegalPage from './LegalPage';
import SecurityPage from './SecurityPage';
import ReplayVideoCard from './ReplayVideoCard';
import HeroFeatures from './HeroFeatures';
import { getAppLogoSrc, getAppLogoSizeStyle } from '../../constants/branding';

type NavigateOptions = {
  hash?: string | null;
};

type PublicThemeMode = 'light' | 'dark';

type LandingNavKey = 'workflow' | 'replay' | 'pricing' | 'help' | null;

const LANDING_SECTION_IDS = ['workflow', 'replay', 'pricing'] as const;

const LANDING_HASH_TO_NAV: Record<string, Exclude<LandingNavKey, null>> = {
  workflow: 'workflow',
  replay: 'replay',
  pricing: 'pricing',
};

type PublicSiteShellProps = {
  path: Exclude<PublicRoutePath, '/login' | '/signup'>;
  hash?: string | null;
  onNavigate: (path: PublicRoutePath, options?: NavigateOptions) => void;
};

type BrokerLogoProps = {
  domain: string;
  name: string;
  className?: string;
};

type HelpLayoutProps = {
  children: ReactNode;
  title: string;
  subtitle: string;
  onBackToHub: () => void;
};

import { getBranding, defaultBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import type { BrandingConfig } from '../../config/branding';

const branding = getBranding(currentRuntime.brand.id);
const PUBLIC_ASSETS = {
  monitorHero: branding.logos?.monitorHero || '/images/monitor-hero.avif',
};

const CustomLogos = {
  tdAmeritrade: () => (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <rect width="100" height="100" fill="#00a100" rx="12" />
      <path d="M15 35h30v8H34v32h-8V43H15v-8zM48 35h18c12 0 18 8 18 20s-6 20-18 20H48V35zm8 32h9c7 0 10-4 10-12s-3-12-10-12h-9v24z" fill="white" />
    </svg>
  ),
  sierraChart: () => (
    <svg viewBox="0 0 100 60" className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 5 L95 55 L5 55 Z" fill="#0046ad" />
      <path d="M50 5 L65 25 L55 28 L48 18 L40 25 L32 20 Z" fill="white" />
    </svg>
  ),
  lightspeed: () => (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <rect width="100" height="100" fill="#0B1528" rx="12" />
      <path d="M22 15h28l-8 45h-20z" fill="#facc15" />
      <path d="M30 60h55L30 85z" fill="#facc15" />
    </svg>
  ),
};

function BrokerLogo({ domain, name, className = '' }: BrokerLogoProps) {
  const [imgStatus, setImgStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [retryCount, setRetryCount] = useState(0);
  const hasCustom = name === 'TD Ameritrade' || name === 'Sierra Chart' || name === 'Lightspeed';
  const providers = [
    `https://unavatar.io/${domain}?fallback=false`,
    `https://www.google.com/s2/favicons?sz=128&domain_url=${domain}`,
  ];

  const handleImageError = () => {
    if (retryCount < providers.length - 1) {
      setRetryCount((prev) => prev + 1);
      return;
    }
    setImgStatus('error');
  };

  return (
    <div className={`group flex cursor-pointer items-center gap-3 grayscale opacity-60 transition-all duration-500 hover:grayscale-0 hover:opacity-100 ${className}`}>
      <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-zinc-100 bg-white p-1.5 shadow-sm transition-transform group-hover:scale-105 group-active:scale-95">
        {hasCustom ? (
          <div className="flex h-full w-full items-center justify-center p-0.5">
            {name === 'TD Ameritrade' ? <CustomLogos.tdAmeritrade /> : null}
            {name === 'Sierra Chart' ? <CustomLogos.sierraChart /> : null}
            {name === 'Lightspeed' ? <CustomLogos.lightspeed /> : null}
          </div>
        ) : (
          <>
            {imgStatus === 'loading' ? (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-50">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-100 border-t-pink-400" />
              </div>
            ) : null}
            {imgStatus !== 'error' ? (
              <img
                src={providers[retryCount]}
                alt={`${name} logo`}
                className={`h-full w-full object-contain transition-opacity duration-500 ${imgStatus === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImgStatus('loaded')}
                onError={handleImageError}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-50 text-[10px] font-bold uppercase tracking-tight text-zinc-300">
                {name.substring(0, 2)}
              </div>
            )}
          </>
        )}
      </div>
      <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-zinc-800 transition-colors group-hover:text-pink-600">{name}</span>
    </div>
  );
}

function ShieldLogo({ className, theme, sizeScale = 1, brand }: { className?: string; theme: PublicThemeMode; sizeScale?: number; brand?: BrandingConfig }) {
  const baseStyle = getAppLogoSizeStyle({ variant: 'symbol', brand });
  const scaledStyle = {
    width: baseStyle.width ? `calc(${baseStyle.width} * ${sizeScale})` : undefined,
    height: baseStyle.height ? `calc(${baseStyle.height} * ${sizeScale})` : undefined,
  };

  return (
    <div className="relative flex items-center justify-center">
      <img src={getAppLogoSrc({ variant: 'symbol', theme, brand })} alt={(brand && brand.appName) || branding.appName || 'Trade-Proof'} className={className} style={scaledStyle} />
    </div>
  );
}

function BrandLockup({ theme, brand }: { theme: PublicThemeMode; brand?: BrandingConfig }) {
  const resolvedBrand = brand ?? branding;

  return (
    <img
      src={getAppLogoSrc({ variant: 'horizontalLockup', theme, brand: resolvedBrand })}
      alt=""
      aria-hidden="true"
      className="block h-auto w-full object-contain"
    />
  );
}

function HelpLayout({ children, title, subtitle, onBackToHub }: HelpLayoutProps) {
  return (
    <div className="mx-auto max-w-5xl px-8 pb-12 pt-48 reveal">
      <button onClick={onBackToHub} className="group mb-12 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-pink-500">
        <ArrowLeft size={14} className="transition-transform group-hover:-tranzinc-x-1" /> Back to Help
      </button>
      <div className="mb-20">
        <h1 className="mb-6 text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">{title}</h1>
        <p className="max-w-3xl text-lg font-medium leading-relaxed text-zinc-500">{subtitle}</p>
      </div>
      <div className="workstation-frame mb-24 rounded-[3rem] border border-zinc-200 bg-white p-10 text-[#0F172A] md:p-16">{children}</div>
    </div>
  );
}

// Help cards removed from the Help hub — content intentionally omitted.

const BROKERS = [
  { name: 'Schwab', domain: 'schwab.com' },
  { name: 'Fidelity', domain: 'fidelity.com' },
  { name: 'Robinhood', domain: 'robinhood.com' },
  { name: 'E*Trade', domain: 'etrade.com' },
  { name: 'Interactive Brokers', domain: 'interactivebrokers.com' },
  { name: 'tastytrade', domain: 'tastytrade.com' },
  { name: 'Public', domain: 'public.com' },
  { name: 'Webull US', domain: 'webull.com' },
  { name: 'Webull Canada', domain: 'webull.ca' },
  { name: 'Questrade', domain: 'questrade.com' },
  { name: 'Wealthsimple', domain: 'wealthsimple.com' },
  { name: 'Trading 212', domain: 'trading212.com' },
  { name: 'Alpaca', domain: 'alpaca.markets' },
  { name: 'DEGIRO', domain: 'degiro.com' },
  { name: 'Zerodha', domain: 'zerodha.com' },
  { name: 'Stake Australia', domain: 'hellostake.com' },
  { name: 'CommSec', domain: 'commsec.com.au' },
  { name: 'Coinbase', domain: 'coinbase.com' },
  { name: 'Kraken', domain: 'kraken.com' },
  { name: 'BUX', domain: 'bux.com' },
  { name: 'AJ Bell', domain: 'ajbell.co.uk' },
  { name: 'Chase', domain: 'chase.com' },
  { name: 'Empower', domain: 'empower.com' },
  { name: 'eToro', domain: 'etoro.com' },
  { name: 'Moomoo', domain: 'moomoo.com' },
  { name: 'TD Ameritrade', domain: 'tdameritrade.com' },
  { name: 'Upstox', domain: 'upstox.com' },
  { name: 'Wells Fargo', domain: 'wellsfargo.com' },
];

function ProductPipelineCard() {
  return (
    <div className="rounded-[2.5rem] border border-zinc-100 bg-[#f6f8fd] p-8 shadow-[0_16px_50px_rgba(15,23,42,0.05)] sm:p-10">
      <div className="mb-8">
        <div className="text-[15px] font-bold tracking-[0.1em] text-zinc-400 sm:text-base">Product Pipeline</div>
      </div>
      <div className="space-y-8">
        <div className="flex items-start gap-4 sm:gap-5">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#f7edf6] text-pink-500 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset]"><Play size={18} strokeWidth={2.2} /></div>
          <div>
            <div className="text-base font-bold leading-tight text-zinc-900 sm:text-[18px]">Replay Improvements</div>
            <div className="mt-1 max-w-[34rem] text-sm leading-relaxed text-zinc-500 sm:text-[15px]">More ways to replay your trades and understand what happened candle by candle.</div>
          </div>
        </div>

        <div className="flex items-start gap-4 sm:gap-5">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#eef3ff] text-[#5b63f6] shadow-[0_1px_0_rgba(255,255,255,0.75)_inset]"><Globe size={18} strokeWidth={2.2} /></div>
          <div>
            <div className="text-base font-bold leading-tight text-zinc-900 sm:text-[18px]">Broker Integrations</div>
            <div className="mt-1 max-w-[34rem] text-sm leading-relaxed text-zinc-500 sm:text-[15px]">More broker connections for smoother trade syncing and less manual importing.</div>
          </div>
        </div>

        <div className="flex items-start gap-4 sm:gap-5">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#eef3f9] text-zinc-500 shadow-[0_1px_0_rgba(255,255,255,0.75)_inset]"><FileCode size={18} strokeWidth={2.2} /></div>
          <div>
            <div className="text-base font-bold leading-tight text-zinc-900 sm:text-[18px]">Dashboard Widgets</div>
            <div className="mt-1 max-w-[34rem] text-sm leading-relaxed text-zinc-500 sm:text-[15px]">Customize your dashboard with the charts, stats, and insights that matter most to you.</div>
          </div>
        </div>

        <div className="flex items-start gap-4 sm:gap-5">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#eafaf0] text-emerald-500 shadow-[0_1px_0_rgba(255,255,255,0.75)_inset]"><BarChart3 size={18} strokeWidth={2.2} /></div>
          <div>
            <div className="text-base font-bold leading-tight text-zinc-900 sm:text-[18px]">Analytics Engine</div>
            <div className="mt-1 max-w-[34rem] text-sm leading-relaxed text-zinc-500 sm:text-[15px]">Smarter insights that help spot repeated habits, mistakes, and performance patterns.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPage({ onNavigate, isDarkTheme }: { onNavigate: PublicSiteShellProps['onNavigate']; isDarkTheme: boolean }) {
  const [isAnnual, setIsAnnual] = useState(false);

  const scrollToWorkflow = () => {
    window.setTimeout(() => {
      document.getElementById('workflow')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <>
      <section className="mx-auto flex max-w-7xl flex-col items-center px-8 pb-20 pt-48 text-center text-[#0F172A]">
        <div className="reveal mb-10 hidden max-w-full items-center gap-2 rounded-full border border-[#7900db] bg-[#7900db] px-3 py-1.5 text-center text-[9px] font-bold uppercase tracking-[0.16em] text-white shadow-sm sm:inline-flex sm:gap-2.5 sm:px-4 sm:text-[10px] sm:tracking-[0.4em]">
          <Activity size={12} strokeWidth={3} className="shrink-0 text-white" /> Performance Intelligence for Serious Traders
        </div>
        <h1 className="headline-gradient reveal mb-12 max-w-5xl text-6xl font-bold leading-[1.1] tracking-tight md:text-7xl lg:text-8xl">Your trades deserve <br /> more than a journal.</h1>
        <p className="reveal delay-100 mb-14 max-w-3xl text-lg font-medium leading-relaxed text-zinc-900 md:text-[22px]">Every trade tells a story. We help you write a better next chapter.</p>
        <div className="reveal delay-200 mb-16 flex flex-col items-center gap-6 sm:flex-row">
          <button
            onClick={() => {
              onNavigate('/');
              scrollToWorkflow();
            }}
            className="group flex w-full items-center justify-center gap-3 whitespace-nowrap rounded-full bg-pink-500 px-12 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white shadow-xl transition-all active:scale-95 hover:-tranzinc-y-0.5 hover:bg-[#c026d3] sm:w-auto"
          >
            Start Your Next Chapter <ChevronRight size={13} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
        <div className="reveal delay-300 relative w-full max-w-6xl">
          <img
            src={PUBLIC_ASSETS.monitorHero}
            alt="Trade-Proof Monitor Hero"
            className="mx-auto block h-auto w-full max-w-[1200px] -mt-8 object-contain transition-opacity duration-700 hover:opacity-100"
          />
        </div>
        <div className="reveal delay-400 mx-auto mt-10 max-w-3xl text-center">
          <h2 className="mb-4 text-2xl font-bold tracking-tight text-zinc-900 md:text-4xl">Capture. Understand. Improve.</h2>
          <p className="mx-auto max-w-2xl text-sm font-medium leading-relaxed text-zinc-600 md:text-[18px]">Capture every trade. Understand every decision. Become a better trader.</p>
        </div>
        <HeroFeatures />
      </section>

      <section className="mt-8 border-y border-zinc-300 bg-zinc-100 py-24">
        <div className="reveal mx-auto max-w-7xl px-8 text-center">
          <span className="mb-6 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">Data Ingestion</span>
          <h2 className="mb-10 text-5xl font-bold tracking-tight text-zinc-900">Broker Sync and CSV Import</h2>
          <div className="mb-10 flex flex-wrap items-center justify-center gap-10 md:gap-14">
            {BROKERS.slice(0, 5).map((broker) => (
              <BrokerLogo key={broker.name} domain={broker.domain} name={broker.name} />
            ))}
          </div>
          <p className="mx-auto mb-8 max-w-xl text-sm font-medium text-zinc-500">Connect supported brokers via secure API for automatic ingestion, or utilize our standardized CSV engine supporting 50+ global trading platforms.</p>
          <button onClick={() => onNavigate('/help/broker-support')} className="mt-4 border-b border-pink-200 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-pink-600 transition-all hover:border-pink-500 hover:text-pink-700">See all supported brokers <ArrowRight size={12} className="mb-0.5 ml-1 inline" /></button>
        </div>
      </section>

      <section id="workflow" className="border-b border-zinc-300 bg-white py-28">
        <div className="mx-auto max-w-7xl px-8">
          <div className="reveal mb-20 text-center lg:text-left">
            <span className="mb-4 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">THE WORKFLOW</span>
            <h2 className="text-5xl font-bold tracking-tight text-zinc-900">From import to insight.</h2>
          </div>
          <div className="workflow-steps-grid grid grid-cols-1 gap-6 lg:grid-cols-4">
            {[
              { label: 'Step 01', title: 'Import Trades', desc: 'Connect a supported broker or upload a CSV file to bring your executions into Trade-Proof.', icon: <Database size={30} /> },
              { label: 'Step 02', title: 'Review On-Chart', desc: 'See entries and exits directly on the chart and review them against market structure.', icon: <Crosshair size={30} /> },
              { label: 'Step 03', title: 'Replay the Trade', desc: 'Step through price action candle by candle to review how the trade actually unfolded.', icon: <History size={30} /> },
              { label: 'Step 04', title: 'Analyze Performance', desc: 'Measure execution, behavior, and performance to see what is helping or hurting results.', icon: <BarChart3 size={30} /> },
            ].map((item, index) => (
              <div key={item.title} className="workflow-step capability-item reveal bg-white/60 p-10 shadow-sm backdrop-blur-sm" style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="mb-10 text-zinc-300">{item.icon}</div>
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-pink-500">{item.label}</div>
                <h3 className="mb-5 text-xl font-bold text-zinc-800">{item.title}</h3>
                <p className="text-sm font-medium leading-relaxed text-zinc-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="replay" className="border-b border-zinc-300 bg-zinc-100 py-28">
        <div className="mx-auto max-w-7xl px-8">
          <div className="flex flex-col items-center gap-20">
            <div className="reveal mx-auto max-w-3xl text-center">
              <span className="mb-6 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">Technical Replay</span>
              <h2 className="mb-10 text-5xl font-bold leading-[1.1] tracking-tight text-zinc-900">Eliminate hindsight bias.</h2>
              <p className="mx-auto mb-12 max-w-xl text-lg font-medium leading-relaxed text-zinc-500">Static charts hide the stress of the live session. Our engine reconstructs the tape so you can audit how you managed the trade while the outcome was still uncertain.</p>
              <button onClick={() => onNavigate('/', { hash: 'pricing' })} className="group mx-auto inline-flex h-[40.5px] items-center gap-3 rounded-full bg-purple-600 px-8 py-0 text-[11px] font-bold uppercase tracking-[0.3em] text-white shadow-[0_12px_30px_rgba(168,85,247,0.28)] transition-all hover:bg-purple-800 active:scale-95">Access the Engine <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></button>
            </div>
          </div>

          <div className="mt-12">
            <div className="relative aspect-video w-full lg:h-[680px] lg:aspect-auto">
              <ReplayVideoCard src="/videos/Replay-Video.webm" alt="Technical replay" isDarkTheme={isDarkTheme} />
            </div>
          </div>
        </div>
      </section>

      <section id="forensics" className="border-y border-zinc-100 bg-white py-28">
        <div className="forensics-layout mx-auto flex max-w-7xl flex-col items-center gap-12 px-8 sm:gap-16 lg:gap-12 lg:flex-row">
          <div className="forensics-visual reveal delay-200 order-2 w-full max-w-[15rem] sm:max-w-[22rem] md:max-w-[26rem] lg:order-1 lg:w-[28rem] lg:max-w-[28rem] lg:flex-none">
            <img src="/images/Execution-Analysis.png" alt="Execution Analysis" className="w-full h-auto object-contain" />
          </div>

          <div className="forensics-copy reveal order-1 text-center lg:order-2 lg:flex-1 lg:text-left">
            <span className="mb-6 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">Execution Analysis</span>
            <h2 className="mb-10 text-5xl font-bold leading-[1.1] tracking-tight text-zinc-900">Behavioral Forensics.</h2>
            <p className="forensics-description mx-auto mb-12 max-w-xl text-lg font-medium leading-relaxed text-zinc-500 lg:mx-0">Go beyond simple profit and loss. Examine how each trade was managed through time to reveal inefficiencies in patience, profit capture, and execution quality. Trade-Proof surfaces the behavioral patterns that quietly shape long-term performance.</p>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="text-center sm:text-left">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">RISK/REWARD RATIO</div>
                <div className="text-5xl font-bold text-zinc-900 leading-none">2.14R</div>
              </div>

              <div className="text-center sm:text-left">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">TICKS CAPTURED</div>
                <div className="text-5xl font-bold text-emerald-500 leading-none">214</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="relative bg-zinc-100 px-8 pb-8 pt-24 text-center text-[#0F172A]">
        <div className="pricing-content reveal mx-auto max-w-6xl">
          <span className="pricing-eyebrow mb-6 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">Pricing</span>
          <h2 className="pricing-title mb-8 text-5xl font-bold tracking-tight text-[#0F172A] md:text-6xl">
            <span className="whitespace-nowrap">One plan.</span>{' '}
            <span className="block whitespace-nowrap sm:inline">Full access.</span>
          </h2>
          <p className="pricing-summary mx-auto mb-16 max-w-2xl text-lg font-medium leading-relaxed text-zinc-500">One plan with everything needed to import, review, replay, and analyze trades.</p>
          <div className="pricing-card group relative mx-auto max-w-5xl mb-16">
            <div className="absolute -inset-4 bg-gradient-to-br from-pink-500/10 via-transparent to-blue-500/10 opacity-50 blur-3xl" />
            <div className="pricing-frame workstation-frame relative flex flex-col overflow-hidden rounded-[3rem] border border-zinc-200 bg-white shadow-2xl md:flex-row">
              <div className="pricing-offer flex flex-col items-center justify-center p-10 text-center md:w-[55%] md:p-16">
                <div className="pricing-toggle mb-12 flex items-center gap-2 rounded-full border border-zinc-200/60 bg-zinc-100 p-1.5 shadow-inner">
                  <button onClick={() => setIsAnnual(false)} className={`rounded-full px-7 py-2 text-[10px] font-bold uppercase tracking-[0.25em] transition-all duration-300 ${!isAnnual ? 'border border-zinc-200/50 bg-white text-[#0F172A] shadow-md' : 'text-zinc-500 hover:text-zinc-700'}`}>Monthly</button>
                  <button onClick={() => setIsAnnual(true)} className={`rounded-full px-7 py-2 text-[10px] font-bold uppercase tracking-[0.25em] transition-all duration-300 ${isAnnual ? 'border border-zinc-200/50 bg-white text-[#0F172A] shadow-md' : 'text-zinc-500 hover:text-zinc-700'}`}>Annual</button>
                </div>
                <div className="pricing-plan-label mb-8 text-[11px] font-bold uppercase tracking-[0.4em] text-zinc-400">Trade-Proof Full Access</div>
                <div className="pricing-price-block mb-12 flex flex-col items-center gap-1.5">
                  <div className="flex items-baseline gap-2.5"><span className="pricing-price text-7xl font-bold tracking-tight text-[#0F172A]">{isAnnual ? '$390' : '$39'}</span><span className="pricing-period text-xl font-bold text-zinc-400">/{isAnnual ? 'year' : 'mo'}</span></div>
                  {isAnnual ? (
                    <div className="mt-3 flex flex-col items-center gap-3">
                      <div className="text-[13px] font-bold uppercase tracking-widest text-zinc-600">Equivalent to $32.50/mo</div>
                      <div className="rounded-xl border border-emerald-200/40 bg-emerald-50 px-4 py-1.5 text-[11px] font-bold tracking-widest text-emerald-600">SAVE $78</div>
                    </div>
                  ) : (
                    <div className="mt-2 text-[13px] font-bold uppercase tracking-widest text-zinc-500">Billed monthly</div>
                  )}
                </div>
                <div className="pricing-cta mx-auto mb-8 flex w-full max-w-[320px] flex-col gap-4">
                  <button className="min-h-[40.5px] w-full min-w-0 rounded-full bg-pink-500 px-4 py-3 text-[10px] font-bold uppercase leading-tight tracking-[0.14em] text-white shadow-lg transition-all active:scale-95 hover:bg-[#c026d3] sm:h-[40.5px] sm:whitespace-nowrap sm:px-8 sm:py-0 sm:text-[11px] sm:tracking-[0.3em]" onClick={() => onNavigate('/signup')}>Start 7-Day Free Trial</button>
                </div>
                <div className="pricing-trial-note text-[10px] font-bold uppercase tracking-[0.2em] text-pink-500 opacity-80">
                  <span className="block sm:hidden">
                    <span className="block">Full access during trial</span>
                    <span className="block">Cancel anytime</span>
                  </span>
                  <span className="hidden sm:inline">Full access during trial • Cancel anytime</span>
                </div>
              </div>
              <div className="pricing-divider my-16 hidden w-[1.5px] bg-zinc-200 opacity-100 md:block" />
              <div className="pricing-features flex flex-col justify-center bg-zinc-50/20 p-10 text-left md:w-[45%] md:p-16">
                <div className="pricing-feature-list space-y-10 text-[#0F172A]">
                  {[
                    { title: 'Broker & CSV import', desc: 'Import trades from supported brokers or CSV files.' },
                    { title: 'On-chart trade review', desc: 'Review entries and exits directly on the chart.' },
                    { title: 'Candle-by-candle replay', desc: 'Replay price action one candle at a time.' },
                    { title: 'Behavior & performance analytics', desc: 'Analyze execution, behavior, and trade outcomes.' },
                  ].map((feature) => (
                    <div key={feature.title} className="pricing-feature flex gap-6">
                      <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-pink-100 text-pink-600 shadow-sm"><Check size={14} strokeWidth={4} /></div>
                      <div>
                        <div className="pricing-feature-title mb-1.5 text-[18px] font-bold leading-tight text-zinc-800">{feature.title}</div>
                        <div className="pricing-feature-description max-w-[220px] text-[15px] font-normal leading-relaxed text-zinc-500">{feature.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="execution-intelligence" className="border-y border-zinc-200 bg-zinc-50 py-20 text-[#0F172A]">
        <div className="mx-auto max-w-7xl px-8">
          <div className="reveal mx-auto flex max-w-4xl flex-col items-center text-center">
            <span className="mb-6 block text-[13px] font-bold uppercase tracking-[0.45em] text-pink-500">Roadmap Preview</span>
            <h2 className="max-w-3xl text-3xl font-bold leading-[1.12] tracking-tight text-zinc-900 md:text-5xl">
              A preview of <span className="whitespace-nowrap">Trade-Proof’s</span> next evolution.
            </h2>
            <p className="mt-6 max-w-2xl text-base font-medium leading-relaxed text-zinc-500 md:text-lg">
              We’re exploring Execution Intelligence™ — automated review engines designed to help traders uncover recurring behavior patterns, compare execution against their plan, and recognize high-risk session behavior.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex max-w-full whitespace-nowrap rounded-full border border-purple-100 bg-purple-50 px-3 py-2 text-[9px] font-bold uppercase tracking-[0.14em] text-purple-600 shadow-sm sm:px-4 sm:text-[10px] sm:tracking-[0.22em]">ROADMAP CONCEPT · IN DEVELOPMENT</span>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'Trade DNA™ Engine',
                description: 'Planned to identify recurring trading habits, strengths, leaks, and execution patterns across your trade history.',
                icon: <BarChart3 size={20} />,
                tone: 'text-pink-600 bg-pink-50 border-pink-100',
                visual: 'dna',
              },
              {
                title: 'Ghost Trade™ Engine',
                description: 'Planned to replay your actual trade beside your rule-based plan, showing where execution drifted from the setup.',
                icon: <Crosshair size={20} />,
                tone: 'text-blue-600 bg-blue-50 border-blue-100',
                visual: 'ghost',
              },
              {
                title: 'Red Zone™ Engine',
                description: 'Planned to flag signs of tilt, revenge trading, overtrading, size spirals, and other high-risk session behavior.',
                icon: <Zap size={20} />,
                tone: 'text-purple-600 bg-purple-50 border-purple-100',
                visual: 'risk',
              },
            ].map((engine, index) => (
              <article
                key={engine.title}
                className="reveal rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-[0_18px_48px_rgba(15,23,42,0.06)]"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-2xl border ${engine.tone}`}>
                    {engine.icon}
                  </div>
                  <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">In development</span>
                </div>

                <div className="mb-5 overflow-hidden rounded-[1.25rem] border border-zinc-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_48%,#eef4ff_100%)] p-4">
                  {engine.visual === 'dna' ? (
                    <div className="relative h-28">
                      <div className="absolute left-[18%] top-4 h-px w-[34%] rotate-12 bg-gradient-to-r from-pink-300 to-blue-300" />
                      <div className="absolute left-[43%] top-12 h-px w-[30%] -rotate-12 bg-gradient-to-r from-purple-300 to-sky-300" />
                      <div className="absolute left-[24%] bottom-6 h-px w-[46%] bg-gradient-to-r from-pink-200 to-purple-300" />
                      {['Timing', 'Size', 'Exit', 'Setup'].map((label, chipIndex) => (
                        <span
                          key={label}
                          className={`absolute rounded-full border border-white bg-white/90 px-3 py-1 text-[10px] font-bold text-zinc-600 shadow-sm ${chipIndex === 0 ? 'left-0 top-0' : chipIndex === 1 ? 'right-2 top-6' : chipIndex === 2 ? 'left-7 bottom-2' : 'right-0 bottom-0'}`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : engine.visual === 'ghost' ? (
                    <div className="flex h-28 flex-col justify-center gap-4">
                      <div>
                        <div className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500"><span>Actual</span><span className="text-pink-500">ACTUAL PATH</span></div>
                        <div className="h-2 overflow-hidden rounded-full bg-zinc-200"><div className="h-full w-[72%] rounded-full bg-gradient-to-r from-pink-400 to-purple-500" /></div>
                      </div>
                      <div>
                        <div className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500"><span>Planned</span><span className="text-blue-500">PLANNED PATH</span></div>
                        <div className="h-2 overflow-hidden rounded-full bg-zinc-200"><div className="h-full w-[88%] rounded-full bg-gradient-to-r from-blue-400 to-sky-400" /></div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-28 flex-col justify-center gap-3">
                      {['Tilt risk', 'Re-entry', 'Size spike'].map((label, chipIndex) => (
                        <div key={label} className="flex items-center gap-3 rounded-2xl border border-white bg-white/85 px-3 py-2 shadow-sm">
                          <span className={`h-2.5 w-2.5 rounded-full ${chipIndex === 0 ? 'bg-rose-400' : chipIndex === 1 ? 'bg-purple-400' : 'bg-blue-400'}`} />
                          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-600">{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold tracking-tight text-zinc-900">{engine.title}</h3>
                <p className="mt-3 text-sm font-medium leading-7 text-zinc-500">{engine.description}</p>
              </article>
            ))}
          </div>

          <p className="reveal mx-auto mt-10 max-w-3xl text-center text-xs font-normal leading-6 text-zinc-500">
            Execution Intelligence™ is currently in development. Feature names, behavior, and availability may change before release.
          </p>
        </div>
      </section>

      <section id="community-roadmap" className="border-y border-zinc-300 bg-white py-28">
        <div className="mx-auto max-w-7xl px-8">
          <div className="flex flex-col items-start gap-12 lg:flex-row lg:items-center lg:gap-20">
            <div className="reveal lg:w-[48%]">
              <span className="mb-5 block text-[13px] font-bold uppercase tracking-[0.5em] text-pink-500">Community Roadmap</span>
              <h2 className="mb-6 text-5xl font-bold leading-[1.1] tracking-tight text-zinc-900">
                Help shape what<br />
                <span className="block"><span className="whitespace-nowrap">Trade-Proof</span> builds next.</span>
              </h2>
              <p className="mb-8 max-w-xl text-lg font-medium leading-relaxed text-zinc-500">Have an idea for a feature that would make Trade-Proof more useful? Share it with us and help shape what we build next.</p>
              <button
                type="button"
                onClick={() => onNavigate('/feature-request')}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-pink-600 px-8 py-3 text-[11px] font-bold uppercase tracking-[0.3em] text-white shadow-xl transition-all active:scale-95 hover:bg-pink-700"
              >
                Suggest a Feature
                <ArrowRight size={14} className="transition-transform hover:translate-x-1" />
              </button>
            </div>

            <div className="reveal lg:w-[52%]">
              <div className="mx-auto lg:ml-8">
                <ProductPipelineCard />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function HelpHubPage() {
  return (
    <div className="mx-auto max-w-6xl px-8 pb-12 pt-48 text-left text-[#0F172A]">
      <div className="reveal mb-24 text-center">
        <h1 className="mb-8 text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Support & Documentation.</h1>
        <p className="mb-12 text-lg font-medium text-zinc-500">Get quick answers to common questions and platform workflows.</p>
      </div>
      {/* Help cards section removed */}

      <div className="mb-24">
        <div className="reveal mb-12 text-center">
          <span className="mb-4 block text-[11px] font-bold uppercase tracking-[0.5em] text-pink-500">Common Questions</span>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900">Frequently Asked Questions.</h2>
        </div>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
          {[
            { q: 'Which brokers and platforms are supported?', a: 'Trade-Proof supports direct integration with selected brokers and platforms, with CSV import available for many additional workflows. Supported broker coverage will continue to expand over time.' },
            { q: 'Can I still use Trade-Proof if my broker is not supported directly?', a: 'Yes. If direct broker sync is not available, you can still import your execution data using CSV and use the full review, replay, and analytics workflow.' },
            { q: 'How does CSV import work?', a: 'Export your execution data from your broker or platform, upload the file into Trade-Proof, and the system will normalize the data for chart review, replay, and performance analysis.' },
            { q: 'Which asset classes can I review in Trade-Proof?', a: 'Trade-Proof supports workflows across Equities, Futures, Forex, and Crypto.' },
            { q: 'What is included in the 7-day free trial?', a: 'The trial includes full access to Trade-Proof’s core workflow, including imports, on-chart review, replay, and analytics.' },
            { q: 'Does the replay engine work during the free trial?', a: 'Yes. Candle-by-candle replay is included during the 7-day trial.' },
            { q: 'How is Trade-Proof different from a basic trading journal?', a: 'Trade-Proof goes beyond static journaling by combining broker or CSV imports, on-chart execution review, candle-by-candle replay, and behavioral analytics in one workflow.' },
            { q: 'Can I switch between monthly and annual billing later?', a: 'Yes. You can change your billing preference from monthly to annual, or annual to monthly, from your account settings.' },
            { q: 'Can I cancel anytime?', a: 'Yes. There are no long-term contracts, and you can cancel before renewal at any time.' },
            { q: 'Is my trade data secure?', a: 'Trade-Proof is designed to handle your execution data with care, controlled access, and secure account-level workflows.' },
          ].map((faq) => (
            <div key={faq.q} className="reveal rounded-2xl border border-zinc-300 bg-zinc-100 p-8 transition-colors hover:border-pink-500 hover:bg-zinc-50">
              <h4 className="mb-4 flex items-start gap-3 text-[13px] font-bold uppercase tracking-widest text-zinc-800"><span className="font-bold text-pink-500">Q.</span> {faq.q}</h4>
              <p className="border-l border-zinc-200 pl-7 text-[13px] font-medium leading-relaxed text-zinc-500">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div id="contact" className="reveal mx-auto mb-12 max-w-4xl text-[#0F172A]">
        <div className="relative overflow-hidden rounded-[48px] border border-zinc-200 bg-white p-8 shadow-sm md:p-12 lg:p-16">
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-50/60 to-transparent" />
          <div className="relative z-10">
            <span className="mb-4 block text-[14px] font-bold uppercase tracking-[0.3em] text-pink-500">Direct Inquiry</span>
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">Contact Support.</h2>
            <p className="mb-10 text-lg font-normal text-zinc-500">Choose an inquiry type and we&apos;ll route it to the right team.</p>
            <PublicContactForm defaultInquiryType="Product Support" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BrokerSupportPage({ onNavigate }: { onNavigate: PublicSiteShellProps['onNavigate'] }) {
  return (
    <div className="mx-auto max-w-6xl px-8 pb-8 pt-48 text-left text-[#0F172A]">
      <button onClick={() => onNavigate('/help')} className="group mb-12 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-pink-500">
        <ArrowLeft size={14} className="transition-transform group-hover:-tranzinc-x-1" /> Back to Help
      </button>
      <div className="reveal mb-24 text-center">
        <h1 className="mb-8 text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Supported Integrations.</h1>
        <p className="mx-auto mb-12 max-w-3xl text-lg font-medium text-zinc-500">Connecting your trading data to Trade-Proof is built to be seamless. We support two primary methods of ingestion.</p>
      </div>

      <div className="reveal mb-24 text-center">
        <h2 className="mb-4 text-2xl font-bold tracking-tight text-zinc-900">Two ways to import your trades.</h2>
        <p className="mb-12 text-sm font-medium text-zinc-500">Connect a supported broker for automatic sync, or upload a CSV file manually.</p>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2">
          <div className="workstation-frame flex flex-col overflow-hidden rounded-[2.5rem] border border-zinc-200 bg-white text-[#0F172A] shadow-xl">
            <div className="flex-grow p-8 pb-4"><div className="mb-6 flex items-center gap-4"><div className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-100 bg-zinc-50 text-pink-500 shadow-sm"><RefreshCw size={18} /></div><h3 className="text-lg font-bold uppercase tracking-wide text-zinc-900">01. Automatic Broker Sync</h3></div><p className="text-left text-sm font-medium leading-relaxed text-zinc-500">Connect a supported broker via secure API and automatically ingest executions for chart review.</p></div>
            <div className="border-t border-zinc-100 bg-zinc-50 px-8 py-4 text-left"><span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Best for supported brokers</span></div>
          </div>
          <div className="workstation-frame flex flex-col overflow-hidden rounded-[2.5rem] border border-zinc-200 bg-white text-[#0F172A] shadow-xl">
            <div className="flex-grow p-8 pb-4"><div className="mb-6 flex items-center gap-4"><div className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-100 bg-zinc-50 text-blue-500 shadow-sm"><FileCode size={18} /></div><h3 className="text-lg font-bold uppercase tracking-wide text-zinc-900">02. Manual CSV Import</h3></div><p className="text-left text-sm font-medium leading-relaxed text-zinc-500">Upload exported execution logs and normalize trades for chart review and analytics.</p></div>
            <div className="border-t border-zinc-100 bg-zinc-50 px-8 py-4 text-left"><span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Best for exported trade logs</span></div>
          </div>
        </div>
      </div>

      <div className="reveal">
        <h3 className="mb-10 text-center text-2xl font-bold tracking-tight text-zinc-900">Global Platform Support.</h3>
        <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 pb-16 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {BROKERS.map((broker, index) => (
            <div key={broker.name} className="reveal" style={{ transitionDelay: `${index * 20}ms` }}>
              <BrokerLogo domain={broker.domain} name={broker.name} />
            </div>
          ))}
        </div>
        <div className="text-center text-xs text-zinc-400 mt-2 mb-10">
          * Broker availability may vary by region and connection support.
        </div>
        <div className="workstation-frame mx-auto mb-12 max-w-3xl rounded-[2.5rem] border border-zinc-200 bg-white p-10 text-center text-[#0F172A]">
          <Globe className="mx-auto mb-6 text-zinc-300" size={32} />
          <h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-800">Don&apos;t see your broker?</h4>
          <p className="mb-8 text-sm font-medium text-zinc-500">You may still be able to import via CSV. Our mapping engine can normalize any export containing timestamps, tickets, symbols, and price data.</p>
          <button onClick={() => onNavigate('/help', { hash: 'contact' })} className="text-[11px] font-bold uppercase tracking-[0.2em] text-pink-600 transition-colors hover:text-pink-700">Request Integration Support</button>
        </div>
      </div>
    </div>
  );
}

function HelpTopicPage({ path, onNavigate }: { path: Exclude<PublicHelpTopicPath, '/help/broker-support'>; onNavigate: PublicSiteShellProps['onNavigate'] }) {
  const backToHub = () => onNavigate('/help');

  switch (path) {
    case '/help/getting-started':
      return (
        <HelpLayout title="Getting Started" subtitle="Follow these initial steps to set up your Trade-Proof workstation and begin your first review session." onBackToHub={backToHub}>
          <div className="space-y-12">
            <div className="flex gap-8 border-b border-zinc-100 pb-12"><div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 font-bold text-white">1</div><div><h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-900">Sync Execution Data</h4><p className="leading-relaxed text-zinc-500">The first step in any session is importing your trades. You can connect a supported broker directly via API or use our CSV ingestion tool to bring in your historical execution logs.</p></div></div>
            <div className="flex gap-8 border-b border-zinc-100 pb-12"><div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 font-bold text-white">2</div><div><h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-900">Audit on the Chart</h4><p className="leading-relaxed text-zinc-500">Once imported, navigate to the Review tab. Trade-Proof maps your entries and exits directly onto institutional-grade charts, allowing you to see your fills relative to market structure.</p></div></div>
            <div className="flex gap-8"><div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 font-bold text-white">3</div><div><h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-900">Analyze Performance</h4><p className="leading-relaxed text-zinc-500">Use the Analytics dashboard to view your MAE/MFE statistics. This data helps you identify if you are over-holding losing trades or cutting winners too early based on behavioral patterns.</p></div></div>
          </div>
        </HelpLayout>
      );
    case '/help/importing-trades':
      return (
        <HelpLayout title="Importing Trades" subtitle="Learn about the two primary methods for bringing your trading data into the Trade-Proof engine." onBackToHub={backToHub}>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2"><div className="rounded-3xl border border-zinc-100 bg-zinc-50 p-8"><RefreshCw className="mb-6 text-pink-500" size={24} /><h4 className="mb-4 text-sm font-bold uppercase tracking-widest text-zinc-900">Broker Sync</h4><p className="text-xs leading-relaxed text-zinc-500">Direct API sync for supported brokers allows for automatic, recurring ingestion of your executions. This is the most efficient path for high-frequency review sessions.</p></div><div className="rounded-3xl border border-zinc-100 bg-zinc-50 p-8"><FileCode className="mb-6 text-blue-500" size={24} /><h4 className="mb-4 text-sm font-bold uppercase tracking-widest text-zinc-900">CSV Ingestion</h4><p className="text-xs leading-relaxed text-zinc-500">Export trade logs from your platform as a .csv file and upload them to our system. Our engine supports 50+ global platform formats.</p></div></div>
        </HelpLayout>
      );
    case '/help/replay-and-review':
      return (
        <HelpLayout title="Replay & Review" subtitle="The core of the workstation. Re-experience every trade to audit your execution and psychological discipline." onBackToHub={backToHub}>
          <div className="space-y-16"><div className="flex flex-col items-center gap-12 md:flex-row"><div className="md:w-1/2"><h4 className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-zinc-900"><Zap className="text-pink-500" size={18} /> Candle-by-Candle Replay</h4><p className="leading-relaxed text-zinc-500">Unlike static journals, Trade-Proof lets you play back the tape. Step through price action one candle at a time to see exactly what you were seeing when you decided to enter or exit.</p></div><div className="flex items-center justify-center rounded-3xl border border-zinc-100 bg-zinc-50 p-8 md:w-1/2"><History size={48} className="text-zinc-200" /></div></div><div className="flex flex-col items-center gap-12 md:flex-row-reverse"><div className="md:w-1/2"><h4 className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-zinc-900"><Crosshair className="text-pink-500" size={18} /> Technical Execution Audit</h4><p className="leading-relaxed text-zinc-500">Overlay your fills on technical market structure. Audit your risk management by seeing exactly where your stop-loss was placed relative to historical support and resistance zones.</p></div><div className="flex items-center justify-center rounded-3xl border border-zinc-100 bg-zinc-50 p-8 md:w-1/2"><BarChart3 size={48} className="text-zinc-200" /></div></div></div>
        </HelpLayout>
      );
    case '/help/billing-and-trial':
      return (
        <HelpLayout title="Billing & Trial" subtitle="Transparency in pricing and account access. Every feature is available during your initial workstation setup." onBackToHub={backToHub}>
          <div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-2"><div><h4 className="mb-4 text-sm font-bold uppercase tracking-widest text-zinc-900">7-Day Free Trial</h4><p className="mb-6 text-sm leading-relaxed text-zinc-500">Experience the full power of Trade-Proof with zero risk. You get unrestricted access to Replay, Analytics, and all Import tools for 7 days.</p><div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-pink-500"><Check size={14} /> Full Access Included</div></div><div><h4 className="mb-4 text-sm font-bold uppercase tracking-widest text-zinc-900">Flexible Subscriptions</h4><p className="mb-6 text-sm leading-relaxed text-zinc-900">After your trial, choose between a $39/mo plan or a $390/year plan ($78 savings). You can switch between these at any time.</p><div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400"><Check size={14} /> Cancel Before Renewal Anytime</div></div></div>
        </HelpLayout>
      );
    case '/help/account-settings':
      return (
        <HelpLayout title="Account Settings" subtitle="Manage your workstation profile, security protocols, and data privacy settings." onBackToHub={backToHub}>
          <div className="space-y-12"><div className="flex gap-8 border-b border-zinc-100 pb-12"><div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400"><Lock size={20} /></div><div><h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-900">Encryption & Privacy</h4><p className="leading-relaxed text-zinc-500">Trade-Proof utilizes institutional-grade encryption for all stored execution data. You have complete control over your trade logs.</p></div></div><div className="flex gap-8 border-b border-zinc-100 pb-12"><div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-50 text-zinc-400"><Settings size={20} /></div><div><h4 className="mb-3 text-lg font-bold uppercase tracking-wide text-zinc-900">Profile Management</h4><p className="leading-relaxed text-zinc-500">Update your contact information, manage active API tokens, and configure workstation time zones.</p></div></div></div>
        </HelpLayout>
      );
    default:
      return null;
  }
}

function FoldViewportDebug() {
  const enabled = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('fold-debug') === '1';
  const [, setRevision] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const update = () => setRevision((revision) => revision + 1);
    const visualViewport = window.visualViewport;
    const screenOrientation = window.screen.orientation;

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    visualViewport?.addEventListener('resize', update);
    screenOrientation?.addEventListener('change', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      visualViewport?.removeEventListener('resize', update);
      screenOrientation?.removeEventListener('change', update);
    };
  }, [enabled]);

  if (!enabled) return null;

  const formatDimension = (value: number | undefined) => (
    typeof value === 'number' ? Math.round(value * 100) / 100 : 'N/A'
  );
  const formatMatch = (query: string) => (window.matchMedia(query).matches ? 'TRUE' : 'FALSE');
  const foldPortraitQuery = '(min-width: 680px) and (max-width: 767px) and (min-height: 600px) and (min-aspect-ratio: 4/5)';
  const foldLandscapeQuery = '(min-width: 800px) and (max-width: 850px) and (min-height: 500px) and (max-height: 650px) and (min-aspect-ratio: 4/3) and (max-aspect-ratio: 7/4)';
  const workflowGrid = document.querySelector<HTMLElement>('.workflow-steps-grid');
  const computedGrid = workflowGrid ? window.getComputedStyle(workflowGrid) : null;
  const workflowStepCount = workflowGrid?.querySelectorAll(':scope > .workflow-step').length ?? 0;
  const horizontalOverflow = Math.max(
    0,
    document.documentElement.scrollWidth - document.documentElement.clientWidth,
  );

  const lines = [
    'FOLD VIEWPORT DEBUG',
    `CSS viewport: ${window.innerWidth} × ${window.innerHeight}`,
    `Document: ${document.documentElement.clientWidth} × ${document.documentElement.clientHeight}`,
    `Visual: ${formatDimension(window.visualViewport?.width)} × ${formatDimension(window.visualViewport?.height)}`,
    `Screen: ${window.screen.width} × ${window.screen.height}`,
    `DPR: ${window.devicePixelRatio}`,
    `Orientation: ${window.screen.orientation?.type ?? 'N/A'}`,
    '',
    'MEDIA QUERIES',
    `min-width 680: ${formatMatch('(min-width: 680px)')}`,
    `max-width 767: ${formatMatch('(max-width: 767px)')}`,
    `min-height 600: ${formatMatch('(min-height: 600px)')}`,
    `aspect 4/5: ${formatMatch('(min-aspect-ratio: 4/5)')}`,
    `PORTRAIT FOLD: ${formatMatch(foldPortraitQuery)}`,
    `LANDSCAPE FOLD: ${formatMatch(foldLandscapeQuery)}`,
    `COMPLETE: ${window.matchMedia(foldPortraitQuery).matches || window.matchMedia(foldLandscapeQuery).matches ? 'TRUE' : 'FALSE'}`,
    '',
    'WORKFLOW',
    `grid found: ${workflowGrid ? 'TRUE' : 'FALSE'}`,
    `step children: ${workflowStepCount}`,
    `columns: ${computedGrid?.gridTemplateColumns ?? 'N/A'}`,
    `rows: ${computedGrid?.gridTemplateRows ?? 'N/A'}`,
    `display: ${computedGrid?.display ?? 'N/A'}`,
    `horizontal overflow: ${horizontalOverflow}px`,
  ];

  return (
    <pre
      aria-label="Fold viewport diagnostics"
      style={{
        position: 'fixed',
        bottom: 8,
        left: 8,
        zIndex: 2147483647,
        width: 'min(360px, calc(100vw - 16px))',
        maxHeight: 'calc(100vh - 16px)',
        margin: 0,
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: 6,
        background: 'rgba(0, 0, 0, 0.88)',
        boxShadow: '0 4px 18px rgba(0, 0, 0, 0.35)',
        color: '#fff',
        padding: '8px 10px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.35,
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        pointerEvents: 'none',
      }}
    >
      {lines.join('\n')}
    </pre>
  );
}

export default function PublicSiteShell({ path, onNavigate, hash }: PublicSiteShellProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [publicMenuOpen, setPublicMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isHelpRoute = path === '/help' || path.startsWith('/help/');
  const [activeNav, setActiveNav] = useState<LandingNavKey>(() => {
    if (isHelpRoute) return 'help';
    if (path === '/') {
      const initialHash = String(hash || '').replace(/^#/, '').trim();
      return LANDING_HASH_TO_NAV[initialHash] || 'workflow';
    }
    return null;
  });
  const [themeMode] = useState<PublicThemeMode>(() => {
    try {
      const saved = window.localStorage.getItem('tt_public_theme_mode');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {}
    return 'light';
  });
  const isDarkTheme = themeMode === 'dark';

  const shellTheme = isDarkTheme
    ? {
        root: 'public-shell-dark bg-[#14001f] text-[#f6efff] selection:bg-fuchsia-300 selection:text-[#14001f]',
        canvasOpacity: 'opacity-55',
        nav: 'bg-[#160223]/88 text-[#f6efff] border-b border-white/8 shadow-[0_18px_48px_rgba(0,0,0,0.32)] backdrop-blur-xl',
        navRule: 'via-fuchsia-400/70',
        link: 'text-fuchsia-100/90 hover:text-white',
        linkActive: 'font-bold text-fuchsia-300',
        ctaPink: 'bg-pink-500 hover:bg-[#c026d3] text-white',
        ctaDark: 'bg-white text-[#14001f] hover:bg-fuchsia-100',
        roundLight: 'border-pink-400 bg-white text-pink-500',
        roundDark: 'border-white bg-bold text-white',
      }
    : {
        root: 'public-shell-light bg-white text-[#0F172A] selection:bg-pink-100 selection:text-pink-700',
        canvasOpacity: 'opacity-90',
        nav: 'bg-transparent text-[#0F172A]',
        navRule: 'via-zinc-400',
        link: 'text-zinc-700 hover:text-pink-600',
        linkActive: 'font-bold text-pink-600',
        ctaPink: 'bg-pink-500 hover:bg-[#c026d3] text-white',
        ctaDark: 'bg-[#0F172A] text-white hover:bg-bold',
        roundLight: 'border-pink-500 bg-white text-pink-500',
        roundDark: 'border-bold bg-bold text-white',
      };

  useEffect(() => {
    try {
      window.localStorage.setItem('tt_public_theme_mode', themeMode);
    } catch {}
  }, [themeMode]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setPublicMenuOpen(false);
  }, [path, hash]);

  useEffect(() => {
    if (!publicMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPublicMenuOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [publicMenuOpen]);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('active');
      });
    }, { threshold: 0.1 });
    reveals.forEach((reveal) => observer.observe(reveal));
    return () => observer.disconnect();
  }, [path]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  useEffect(() => {
    if (isHelpRoute) {
      setActiveNav('help');
      return;
    }

    if (path !== '/') {
      setActiveNav(null);
      return;
    }

    const hashId = String(hash || '').replace(/^#/, '').trim();
    const nextNav = LANDING_HASH_TO_NAV[hashId] || 'workflow';
    setActiveNav(nextNav);
  }, [hash, isHelpRoute, path]);

  useEffect(() => {
    const hashId = String(hash || window.location.hash || '').replace(/^#/, '').trim();
    if (!hashId) return;
    const timer = window.setTimeout(() => {
      const element = document.getElementById(hashId);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [path, hash]);

  useEffect(() => {
    if (path !== '/') return;

    const updateActiveNav = () => {
      const triggerY = Math.max(120, window.innerHeight * 0.28);
      let nextNav: Exclude<LandingNavKey, null> = 'workflow';

      for (const sectionId of LANDING_SECTION_IDS) {
        const element = document.getElementById(sectionId);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top <= triggerY) {
          nextNav = sectionId;
        }
      }

      setActiveNav((current) => (current === nextNav ? current : nextNav));
    };

    const handleScroll = () => {
      window.requestAnimationFrame(updateActiveNav);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [path]);

  useEffect(() => {
    const updateScrollTopVisibility = () => {
      setShowScrollTop(window.scrollY > 360);
    };

    updateScrollTopVisibility();
    window.addEventListener('scroll', updateScrollTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollTopVisibility);
  }, [path]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let animationFrameId = 0;
    const pricePoints: number[] = [];
    const maxPoints = 180;
    let currentPrice = 0.5;
    let momentum = 0;
    let lineOffset = 0;
    const lineSpeed = 0.6;

    const updatePrice = () => {
      const volatility = 0.015;
      momentum += (Math.random() - 0.5) * 0.005;
      momentum *= 0.92;
      currentPrice += momentum + (Math.random() - 0.5) * volatility;
      if (currentPrice > 0.85) currentPrice -= 0.05;
      if (currentPrice < 0.15) currentPrice += 0.05;
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      if (pricePoints.length === 0) {
        for (let index = 0; index < maxPoints; index += 1) {
          updatePrice();
          pricePoints.push(currentPrice);
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const step = 140;
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = isDarkTheme ? 'rgba(248, 113, 113, 0.08)' : 'rgba(15, 23, 42, 0.02)';
      for (let index = -height; index < width; index += step) {
        ctx.beginPath();
        ctx.moveTo(index, 0);
        ctx.lineTo(index + height, height);
        ctx.stroke();
      }

      const pointSpacing = width / (maxPoints - 1);
      lineOffset += lineSpeed;
      while (lineOffset >= pointSpacing) {
        lineOffset -= pointSpacing;
        updatePrice();
        pricePoints.push(currentPrice);
        if (pricePoints.length > maxPoints) pricePoints.shift();
      }

      ctx.beginPath();
      for (let index = 0; index < pricePoints.length; index += 1) {
        const x = index * pointSpacing - lineOffset;
        const y = height - pricePoints[index] * height;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.lineWidth = 1.8;
      const lineGradient = ctx.createLinearGradient(0, 0, width, 0);
      lineGradient.addColorStop(0, '#6366f1');
      lineGradient.addColorStop(0.5, '#a855f7');
      lineGradient.addColorStop(1, '#ec48e8');
      ctx.strokeStyle = lineGradient;
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      const gradient = ctx.createLinearGradient(0, height * 0.3, 0, height);
      gradient.addColorStop(0, isDarkTheme ? 'rgba(236, 72, 153, 0.16)' : 'rgba(236, 72, 153, 0.08)');
      gradient.addColorStop(1, isDarkTheme ? 'rgba(20, 0, 31, 0)' : 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fill();
      animationFrameId = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', resize);
    resize();
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkTheme]);

  const renderRoute = () => {
    if (path === '/') return <LandingPage onNavigate={onNavigate} isDarkTheme={isDarkTheme} />;
    if (path === '/legal') return <LegalPage onNavigate={onNavigate} />;
    if (path === '/security') return <SecurityPage onNavigate={onNavigate} />;
    if (path === '/feature-request') return <FeatureRequestPage onNavigate={onNavigate} />;
    if (path === '/help') return <HelpHubPage />;
    if (path === '/help/broker-support') return <BrokerSupportPage onNavigate={onNavigate} />;
    return <HelpTopicPage path={path as Exclude<PublicHelpTopicPath, '/help/broker-support'>} onNavigate={onNavigate} />;
  };

  return (
    <div className={`relative min-h-screen overflow-x-hidden font-sans antialiased ${shellTheme.root}`}>
      {path === '/' ? <FoldViewportDebug /> : null}
      <style dangerouslySetInnerHTML={{ __html: `.workstation-frame { box-shadow: 0 40px 100px -20px rgba(15, 23, 42, 0.08), 0 10px 30px -5px rgba(15, 23, 42, 0.04); background: rgba(255, 255, 255, 0.5); backdrop-filter: blur(8px); } .reveal { opacity: 0; transform: tranzincY(10px); transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); } .reveal.active { opacity: 1; transform: tranzincY(0); } .headline-gradient { background: linear-gradient(135deg, #1e40af 0%, #7c3aed 50%, #db2777 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; padding-bottom: 0.15em; } .capability-item { border-left: 1px solid rgba(15, 23, 42, 0.05); transition: all 0.4s ease; } .capability-item:hover { border-left-color: #ec4899; background: rgba(236, 72, 153, 0.02); } .top-nav-blur { background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); } .top-nav-blur-dark { background: rgba(22, 2, 35, 0.88); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); } .public-shell-dark .headline-gradient { background: linear-gradient(135deg, #f9a8d4 0%, #c084fc 46%, #f472b6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; } .public-shell-dark .workstation-frame { box-shadow: 0 40px 120px -30px rgba(0, 0, 0, 0.55), 0 16px 40px -16px rgba(236, 72, 153, 0.18); background: rgba(31, 13, 53, 0.78); border-color: rgba(255, 255, 255, 0.08); } .public-shell-dark .capability-item { border-left-color: rgba(255, 255, 255, 0.08); } .public-shell-dark .capability-item:hover { border-left-color: #f472b6; background: rgba(255, 255, 255, 0.03); } .public-shell-dark .text-zinc-900, .public-shell-dark .text-zinc-800, .public-shell-dark .text-zinc-700 { color: #f7f0ff !important; } .public-shell-dark .text-zinc-600, .public-shell-dark .text-zinc-500, .public-shell-dark .text-zinc-400 { color: rgba(233, 221, 255, 0.72) !important; } .public-shell-dark [class*="text-[#0F172A]"] { color: #f7f0ff !important; } .public-shell-dark .border-zinc-100, .public-shell-dark .border-zinc-200 { border-color: rgba(255, 255, 255, 0.08) !important; } .public-shell-dark .bg-white, .public-shell-dark .bg-white/60, .public-shell-dark .bg-white/50 { background-color: rgba(31, 13, 53, 0.86) !important; } .public-shell-dark .bg-zinc-50, .public-shell-dark .bg-zinc-100 { background-color: rgba(25, 9, 43, 0.92) !important; } .public-shell-dark .bg-zinc-200 { background-color: rgba(255, 255, 255, 0.12) !important; }` }} />
      {path !== '/legal' ? (
        <canvas ref={canvasRef} className={`pointer-events-none fixed inset-0 z-0 ${shellTheme.canvasOpacity}`} />
      ) : null}
      <nav className={`fixed top-0 z-50 w-full transition-[background-color,padding,box-shadow] duration-500 ${scrolled ? (isDarkTheme ? 'top-nav-blur-dark py-3 shadow-sm' : 'top-nav-blur py-3 shadow-sm') : 'bg-transparent py-3 sm:py-4 xl:py-6'}`}>
        <div className="relative mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 px-4 text-[12px] font-bold uppercase tracking-[0.2em] sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-x-3 sm:px-6 lg:px-8 min-[1180px]:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] min-[1180px]:gap-x-5">
          <button
            type="button"
            aria-label="Trade-Proof home"
            onClick={() => onNavigate('/')}
            className="min-w-0 w-[clamp(172px,58vw,220px)] max-w-full justify-self-start rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:w-[clamp(180px,28vw,220px)]"
          >
            <BrandLockup theme={isDarkTheme ? 'dark' : 'light'} brand={path === '/' ? defaultBranding : undefined} />
          </button>
          <button
            type="button"
            aria-label={publicMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={publicMenuOpen}
            aria-controls="public-navigation-menu"
            onClick={() => setPublicMenuOpen((open) => !open)}
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-[border-color,background-color,color,transform] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:col-start-3 sm:row-start-1 min-[1180px]:hidden ${
              publicMenuOpen
                ? 'border-pink-500 bg-pink-500 text-white'
                : isDarkTheme
                  ? 'border-white/20 bg-[#160223]/80 text-fuchsia-100'
                  : 'border-zinc-300 bg-white/90 text-[#0F172A]'
            }`}
          >
            {publicMenuOpen ? <X aria-hidden="true" size={20} /> : <Menu aria-hidden="true" size={20} />}
          </button>
          <div className="hidden items-center gap-4 tracking-[0.2em] min-[1180px]:col-start-2 min-[1180px]:row-start-1 min-[1180px]:flex min-[1180px]:justify-self-center min-[1366px]:gap-5 2xl:gap-6 2xl:tracking-[0.25em]">
              <button
                onClick={() => {
                  setActiveNav('workflow');
                  onNavigate('/', { hash: 'workflow' });
                }}
                aria-current={activeNav === 'workflow' ? 'page' : undefined}
                className={`uppercase transition-colors ${activeNav === 'workflow' ? shellTheme.linkActive : shellTheme.link}`}
              >
                PLATFORM
              </button>
              <div className={`h-3 w-[1px] ${isDarkTheme ? 'bg-white/12' : 'bg-zinc-200'}`} />
              <button
                onClick={() => {
                  setActiveNav('replay');
                  onNavigate('/', { hash: 'replay' });
                }}
                aria-current={activeNav === 'replay' ? 'page' : undefined}
                className={`uppercase transition-colors ${activeNav === 'replay' ? shellTheme.linkActive : shellTheme.link}`}
              >
                REPLAY
              </button>
              <div className={`h-3 w-[1px] ${isDarkTheme ? 'bg-white/12' : 'bg-zinc-200'}`} />
              <button
                onClick={() => {
                  setActiveNav('pricing');
                  onNavigate('/', { hash: 'pricing' });
                }}
                aria-current={activeNav === 'pricing' ? 'page' : undefined}
                className={`uppercase transition-colors ${activeNav === 'pricing' ? shellTheme.linkActive : shellTheme.link}`}
              >
                PRICING
              </button>
              <div className={`h-3 w-[1px] ${isDarkTheme ? 'bg-white/12' : 'bg-zinc-200'}`} />
              <button
                onClick={() => {
                  setActiveNav('help');
                  onNavigate('/help');
                }}
                aria-current={activeNav === 'help' ? 'page' : undefined}
                className={`uppercase transition-colors ${activeNav === 'help' ? shellTheme.linkActive : shellTheme.link}`}
              >
                HELP
              </button>
          </div>
          <div className="col-span-2 row-start-2 grid w-full min-w-0 max-w-[480px] justify-self-center grid-cols-2 gap-2 sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:ml-auto sm:w-auto sm:max-w-none sm:justify-self-auto sm:flex sm:items-center sm:gap-3 min-[1180px]:col-start-3 min-[1180px]:justify-self-end">
              <button
                className={`h-[40.5px] min-h-[40.5px] min-w-0 whitespace-nowrap rounded-full px-3 text-[10px] uppercase tracking-[0.16em] shadow-lg transition-[background-color,color,transform] active:scale-[0.98] sm:px-5 sm:text-[11px] lg:px-6 xl:px-7 xl:text-[12px] ${shellTheme.ctaPink}`}
                onClick={() => onNavigate('/login')}
              >
                LOG IN
              </button>
              <button
                onClick={() => onNavigate('/signup')}
                className={`h-[40.5px] min-h-[40.5px] min-w-0 whitespace-nowrap rounded-full px-3 text-[10px] uppercase leading-tight tracking-[0.14em] shadow-xl transition-[background-color,color,transform] active:scale-[0.98] sm:px-5 sm:text-[11px] sm:tracking-[0.16em] lg:px-6 xl:px-7 xl:text-[12px] ${shellTheme.ctaDark}`}
              >
                7-DAY FREE TRIAL
              </button>
          </div>
        </div>
        <div
          id="public-navigation-menu"
          hidden={!publicMenuOpen}
          className={`absolute left-4 right-4 top-full mt-1 overflow-hidden rounded-2xl border p-2 shadow-2xl backdrop-blur-xl sm:left-auto sm:right-6 sm:w-72 lg:right-8 min-[1180px]:hidden ${
            isDarkTheme
              ? 'border-white/10 bg-[#160223]/95'
              : 'border-zinc-200 bg-white/95'
          }`}
        >
          {([
            ['workflow', 'PLATFORM', '/', 'workflow'],
            ['replay', 'REPLAY', '/', 'replay'],
            ['pricing', 'PRICING', '/', 'pricing'],
            ['help', 'HELP', '/help', null],
          ] as const).map(([key, label, destination, destinationHash]) => (
            <button
              key={key}
              type="button"
              aria-current={activeNav === key ? 'page' : undefined}
              onClick={() => {
                setActiveNav(key);
                setPublicMenuOpen(false);
                onNavigate(destination, destinationHash ? { hash: destinationHash } : undefined);
              }}
              className={`flex min-h-11 w-full items-center rounded-xl px-4 text-left text-[11px] uppercase tracking-[0.18em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pink-500 ${
                activeNav === key
                  ? isDarkTheme
                    ? 'bg-white/8 text-fuchsia-300'
                    : 'bg-pink-50 text-pink-600'
                  : shellTheme.link
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={`absolute bottom-[3.65rem] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent ${shellTheme.navRule} to-transparent opacity-100 sm:bottom-2`} />
      </nav>
      <div id="top" className="relative z-10">{renderRoute()}</div>
      <footer className="relative z-10 w-full border-t border-zinc-300/80 bg-zinc-200 text-zinc-700">
        <div className="mx-auto max-w-5xl px-6 py-6 sm:py-8">
          <div className="flex flex-col items-center gap-6 text-center">
            <button onClick={() => onNavigate('/')} className="mb-0 flex cursor-pointer items-center gap-1 opacity-40 grayscale transition-opacity hover:opacity-100">
              <ShieldLogo className="w-auto" sizeScale={0.8} theme={isDarkTheme ? 'dark' : 'light'} brand={path === '/' ? defaultBranding : undefined} />
              <span className="text-[11px] font-bold tracking-[0.5em]">TRADEPROOF</span>
            </button>

            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-[9px] font-medium uppercase tracking-[0.14em] text-zinc-500 sm:gap-4 sm:text-[11px] sm:tracking-[0.24em]">
              <a href="/help" onClick={(event) => { event.preventDefault(); onNavigate('/help'); }} className="whitespace-nowrap transition-colors hover:text-pink-600">Support</a>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-zinc-300" />
              <a href="/legal#privacy" onClick={(event) => { event.preventDefault(); onNavigate('/legal', { hash: 'privacy' }); }} className="whitespace-nowrap transition-colors hover:text-pink-600">Privacy Policy</a>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-zinc-300" />
              <a href="/legal#terms" onClick={(event) => { event.preventDefault(); onNavigate('/legal', { hash: 'terms' }); }} className="whitespace-nowrap transition-colors hover:text-pink-600">Terms of Service</a>
            </div>

            <div className="text-[12px] text-zinc-400/80">© 2026 Trade-Proof</div>
          </div>
        </div>
      </footer>
      {showScrollTop ? (
        <button
          type="button"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-5 right-5 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border border-pink-200 bg-white/95 text-pink-600 shadow-lg backdrop-blur transition-[background-color,transform,box-shadow] hover:-translate-y-0.5 hover:bg-pink-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 active:translate-y-0"
        >
          <ArrowUp className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
